import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'framer-motion'
import { supabase } from '../services/supabase'
import { useOrganizationId } from '../hooks/useOrganizationId'
import { useOrganization } from '../contexts/OrganizationContext'
import Card from '../components/Card'
import Button from '../components/Button'
import NutritionTherapy from '../components/NutritionTherapy'
import CarePlanProgressDashboard from '../components/CarePlanProgressDashboard'
import CarePlanOutcomes from '../components/CarePlanOutcomes'
import CarePlanTemplateSelector from '../components/CarePlanTemplateSelector'
import CarePlanAnalytics from '../components/CarePlanAnalytics'
import {
  ArrowLeft,
  Heart,
  UtensilsCrossed,
  BarChart3,
  Target,
  FileText,
  TrendingUp,
  Calendar,
  User,
} from 'lucide-react'
import { formatDate } from '../utils/helpers'

const CarePlanDetails = () => {
  const { t } = useTranslation()
  const { id } = useParams()
  const navigate = useNavigate()
  const organizationId = useOrganizationId()
  const { canWrite } = useOrganization()
  const [plan, setPlan] = useState(null)
  const [client, setClient] = useState(null)
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState('overview')
  const [showTemplates, setShowTemplates] = useState(false)

  useEffect(() => {
    if (id && organizationId) {
      fetchCarePlan()
    }
  }, [id, organizationId])

  const fetchCarePlan = async () => {
    if (!id || !organizationId) return
    try {
      setLoading(true)
      const { data: planData, error: planError } = await supabase
        .from('care_plans')
        .select('*, clients(*)')
        .eq('id', id)
        .eq('organization_id', organizationId)
        .single()

      if (planError) throw planError
      setPlan(planData)
      setClient(planData.clients)
    } catch (error) {
      console.error('Error fetching care plan:', error)
      alert(t('carePlans.loadError'))
    } finally {
      setLoading(false)
    }
  }

  const tabs = [
    { id: 'overview', label: t('carePlans.details.tabs.overview'), icon: FileText },
    { id: 'nutrition', label: t('carePlans.details.tabs.nutrition'), icon: UtensilsCrossed },
    { id: 'progress', label: t('carePlans.details.tabs.progress'), icon: BarChart3 },
    { id: 'outcomes', label: t('carePlans.details.tabs.outcomes'), icon: Target },
    { id: 'analytics', label: t('carePlans.details.tabs.analytics'), icon: TrendingUp },
    { id: 'templates', label: t('carePlans.details.tabs.templates'), icon: FileText },
  ]

  if (loading) {
    return (
      <div className="section-container">
        <p className="text-gray-500 dark:text-gray-400">{t('carePlans.details.loading')}</p>
      </div>
    )
  }

  if (!plan) {
    return (
      <div className="section-container">
        <p className="text-red-500 dark:text-red-400">{t('carePlans.details.notFound')}</p>
        <Button className="mt-4" onClick={() => navigate('/care-plans')}>
          {t('carePlans.details.backToCarePlans')}
        </Button>
      </div>
    )
  }

  return (
    <div className="section-container space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="secondary" className="flex items-center gap-2" onClick={() => navigate(-1)}>
          <ArrowLeft size={18} />
          {t('carePlans.details.back')}
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h1 className={`text-3xl font-bold ${plan.archived_at ? 'text-gray-500 dark:text-gray-400 line-through' : 'text-gray-900 dark:text-white'}`}>
              {plan.title}
            </h1>
            {plan.archived_at && (
              <span className="px-3 py-1 text-sm font-medium bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded">
                {t('carePlans.archived') || 'Archived'}
              </span>
            )}
            {!plan.client_id && !plan.archived_at && (
              <span className="px-3 py-1 text-sm font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded">
                {t('carePlans.general') || 'General'}
              </span>
            )}
          </div>
          <div className="flex items-center gap-4 mt-2 text-sm text-gray-600 dark:text-gray-300">
            <span className="flex items-center gap-1">
              <User size={16} />
              {client ? `${client.first_name} ${client.last_name}` : plan.archived_at ? t('carePlans.clientArchived') || 'Client archived' : t('carePlans.generalCarePlan') || 'General Care Plan'}
            </span>
            <span className="flex items-center gap-1">
              <Calendar size={16} />
              {t('carePlans.details.started')}: {formatDate(plan.start_date)}
            </span>
            <span
              className={`px-3 py-1 rounded-full text-xs font-semibold capitalize ${
                plan.status === 'active'
                  ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                  : plan.status === 'completed'
                  ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                  : 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300'
              }`}
            >
              {plan.status}
            </span>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200 dark:border-gray-700 overflow-x-auto">
        {tabs.map((tab) => {
          const Icon = tab.icon
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors duration-200 whitespace-nowrap ${
                activeTab === tab.id
                  ? 'border-b-2 border-primary-600 dark:border-primary-400 text-primary-700 dark:text-primary-400'
                  : 'text-gray-600 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 hover:border-primary-300 dark:hover:border-primary-600'
              }`}
            >
              <Icon size={18} />
              {tab.label}
            </button>
          )
        })}
      </div>

      {/* Tab Content */}
      <AnimatePresence mode="wait">
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.2 }}
        >
          {activeTab === 'overview' && (
            <div className="space-y-6">
              <Card variant="soft" className="p-6">
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">{t('carePlans.details.summary')}</h3>
                <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{plan.summary}</p>
              </Card>

              {plan.goals && (
                <Card variant="soft" className="p-6">
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                    <Target size={20} />
                    {t('carePlans.details.goals')}
                  </h3>
                  <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{plan.goals}</p>
                </Card>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card variant="soft" className="p-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">{t('carePlans.details.timeline')}</h3>
                  <div className="space-y-2 text-sm">
                    <div>
                      <p className="text-gray-500 dark:text-gray-400">{t('carePlans.details.startDate')}</p>
                      <p className="font-semibold text-gray-900 dark:text-white">{formatDate(plan.start_date)}</p>
                    </div>
                    {plan.end_date && (
                      <div>
                        <p className="text-gray-500 dark:text-gray-400">{t('carePlans.details.endDate')}</p>
                        <p className="font-semibold text-gray-900 dark:text-white">{formatDate(plan.end_date)}</p>
                      </div>
                    )}
                    <div>
                      <p className="text-gray-500 dark:text-gray-400">{t('carePlans.details.status')}</p>
                      <span
                        className={`inline-block px-3 py-1 rounded-full text-xs font-semibold capitalize ${
                          plan.status === 'active'
                            ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                            : plan.status === 'completed'
                            ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                            : 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300'
                        }`}
                      >
                        {plan.status}
                      </span>
                    </div>
                  </div>
                </Card>

                <Card variant="soft" className="p-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">{t('carePlans.details.clientInformation')}</h3>
                  {client ? (
                    <div className="space-y-2 text-sm">
                      <div>
                        <p className="text-gray-500 dark:text-gray-400">{t('carePlans.details.name')}</p>
                        <p className="font-semibold text-gray-900 dark:text-white">
                          {client.first_name} {client.last_name}
                        </p>
                      </div>
                      {client.email && (
                        <div>
                          <p className="text-gray-500 dark:text-gray-400">{t('carePlans.details.email')}</p>
                          <p className="font-semibold text-gray-900 dark:text-white">{client.email}</p>
                        </div>
                      )}
                      {client.phone && (
                        <div>
                          <p className="text-gray-500 dark:text-gray-400">{t('carePlans.details.phone')}</p>
                          <p className="font-semibold text-gray-900 dark:text-white">{client.phone}</p>
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 dark:text-gray-400">{t('carePlans.details.clientInfoNotAvailable')}</p>
                  )}
                </Card>
              </div>
            </div>
          )}

          {activeTab === 'nutrition' && (
            <NutritionTherapy carePlanId={id} clientId={plan.client_id} />
          )}

          {activeTab === 'progress' && (
            <CarePlanProgressDashboard carePlanId={id} clientId={plan.client_id} />
          )}

          {activeTab === 'outcomes' && (
            <CarePlanOutcomes carePlanId={id} clientId={plan.client_id} />
          )}

          {activeTab === 'analytics' && (
            <CarePlanAnalytics carePlanId={id} />
          )}

          {activeTab === 'templates' && (
            <Card variant="soft" className="p-6">
              <CarePlanTemplateSelector
                onSelectTemplate={(template) => {
                  // Load template data into current plan
                  if (template.template_data) {
                    // Could update the plan with template data
                    alert(t('carePlans.details.templateSelected', { name: template.name }))
                  }
                }}
                currentPlanData={{
                  title: plan.title,
                  summary: plan.summary,
                  goals: plan.goals,
                  status: plan.status,
                }}
              />
            </Card>
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  )
}

export default CarePlanDetails

